import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';

export default function SearchableSelect({
  value = '',
  onChange,
  options = [],
  placeholder = '—',
  disabled = false,
  loading = false,
  emptyLabel = 'No options available',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => { setHighlight(-1); }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById('ss-portal');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  useEffect(() => {
    if (highlight >= 0 && listRef.current) {
      const el = listRef.current.children[highlight];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight]);

  const select = (val) => {
    onChange(val);
    setQuery('');
    setOpen(false);
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0 && filtered[highlight]) { e.preventDefault(); select(filtered[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const displayValue = open ? query : (value || '');

  const dropdownEl = open && dropPos && createPortal(
    <div
      id="ss-portal"
      className="ss-drop"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width }}
    >
      <div ref={listRef} className="ss-drop-list">
        {loading && <div className="ss-empty">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div className="ss-empty">{query ? 'No matches' : emptyLabel}</div>
        )}
        {!loading && filtered.map((opt, i) => (
          <div
            key={opt}
            className={`ss-opt${opt === value ? ' sel' : ''}${i === highlight ? ' hl' : ''}`}
            onMouseDown={(e) => { e.preventDefault(); select(opt); }}
            onMouseEnter={() => setHighlight(i)}
          >
            {opt}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <div className="ss-wrap" ref={wrapRef}>
        <div className={`ss-control${open ? ' open' : ''}${disabled ? ' disabled' : ''}`}>
          <input
            ref={inputRef}
            type="text"
            className="ss-input"
            value={displayValue}
            placeholder={loading ? 'Loading...' : placeholder}
            disabled={disabled}
            onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => { setQuery(value || ''); setOpen(true); }}
            onKeyDown={handleKey}
            autoComplete="off"
          />
          {value && !disabled && (
            <button
              type="button"
              className="ss-clear"
              onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); }}
              tabIndex={-1}
            >×</button>
          )}
          <span className="ss-chevron" onClick={() => { if (!disabled) { setOpen(!open); inputRef.current?.focus(); } }}>▾</span>
        </div>
      </div>
      {dropdownEl}
    </>
  );
}
